// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { Browser } from "@lib/browser/Browser";
import { Port } from "@lib/browser/BrowserApi";
import { Socket } from "@lib/messaging/Socket";
import { EventListener } from "@lib/utilities/EventListener";
import { IEventListener } from "@lib/utilities/IEventListener";

/**
 * Provides an API for sending and receiving messages over a message port.
 * Used for communications inside the web extension and to the native messaging hosts.
 */
export class PortSocket extends Socket
{
  readonly port: Port;

  private readonly _onDisconnect: EventListener<(errorMessage: Nullable<string>) => void> = new EventListener();

  private _isConnected: boolean = true;

  constructor(port: Port)
  {
    super();
    this.port = port;

    this.port.onDisconnect.addListener(() => this.handleDisconnect());
    this.port.onMessage.addListener((message) => this.handleMessage(message));
  }

  get isConnected(): boolean
  {
    return this._isConnected;
  }

  get onDisconnect(): IEventListener<(errorMessage: Nullable<string>) => void>
  {
    return this._onDisconnect;
  }

  disconnect(): void
  {
    if (!this._isConnected)
      return;

    this.port.disconnect();
    this.handleDisconnect();
  }

  sendMessage(message: object): void
  {
    if (this._isConnected)
      this.port.postMessage(message);
  }

  private handleDisconnect()
  {
    let errorMessage: Optional<string>;
    if (Browser.runtime.lastError)
      errorMessage = Browser.runtime.lastError.message;
    else if (this.port.error)
      errorMessage = this.port.error.message;

    this._isConnected = false;
    this._onDisconnect.dispatch(errorMessage || null);
    this._onDisconnect.clearListeners();
  }

  private handleMessage(message: object)
  {
    this._onMessage.dispatch(message);
  }
}